import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable, Subject, switchMap, tap } from 'rxjs';
import { ConfigService } from '../config.service';
import { PartnerEvent, ProfilesPartnerNeed } from './commonInterfaces';
import { TripDetail } from './commonInterfaces';
import { DataTransform, DateField } from './dataTransformFunctions';
import { PhoneTypes } from './types/contacts';
import { ProfilesContact, ProfilesPartner, PartnerFile } from './types/partnerships';

export interface PartnerSupporter {
  id: number;
  firstname: string | null;
  lastname: string | null;
  email: string | null;
  subscriptionLevel: string | null;
}

export interface PartnerPhotosStatus {
  bucketConfigured: boolean;
  bucketExists?: boolean;
  prefix?: string;
  fileCount?: number;
  error?: string;
}

export interface PartnerTeams {
  groupName: string
  tripID: number
  tripDate: string | Date
  custID: number
  staffID: number
  tripLeader: string
}

export interface PartnerContact {
  id: string
  prefix: string | null
  jobtitle: string | null
  phone1: string | null
  email: string | null
  role: string | null
  ppm365: number | null
  newuserId: number | null
  contactName: string | null
}

export type FullPartnerContact = ProfilesContact

export interface PartnerContactUpdatedArgs {
  partnerId: string;
  contactId: string;
  operation: "added" | "updated" | "deleted"
}

export interface CreatePartnerContactParams {
  partnerId: string
  firstname: string
  lastname: string
  middlename?: string | null
  prefix?: string | null
  jobtitle?: string | null
  role?: string | null
  address1?: string | null
  address2?: string | null
  city?: string | null
  state?: string | null
  zipcode?: string | null
  district?: string | null
  phone1?: string | null
  phone1type?: PhoneTypes | null
  phone2?: string | null
  phone2type?: PhoneTypes | null
  phone3?: string | null
  phone3type?: PhoneTypes | null
  phone4?: string | null
  phone4type?: PhoneTypes | null
  phone5?: string | null
  phone5type?: PhoneTypes | null
  fax?: string | null
  email?: string | null
  gender?: number | null
  birthday?: Date | null
  facebook?: string | null
  twitter?: string | null
  googleplus?: string | null
  youtube?: string | null
  othersocial?: string | null
  description?: string | null
  descriptionPublic?: string | null
  active?: number
  lat?: number | null
  lng?: number | null
}

export interface UpdatePartnerContactParams extends CreatePartnerContactParams {
  id: string
}

@Injectable({
    providedIn: 'root'
  })

  export class ProfilesPartnerService {
    private _profilesPartnerSubject = new Subject<ProfilesPartner[]>();
    profilesPartner$ = this._profilesPartnerSubject.asObservable();

    partnerUpdated$ = new Subject<ProfilesPartner>();

    partnerContactUpdated$ = new Subject<PartnerContactUpdatedArgs>();

    partnerNeedUpdated$ = new Subject<{ partnerId: string; needId: string; operation: 'added' | 'updated' | 'deactivated' }>();

    partnerEventUpdated$ = new Subject<{ partnerId: string; eventId: string; operation: 'added' | 'updated' | 'deactivated' }>();

    constructor(
      private http: HttpClient,
      private config: ConfigService
    ) {}

    emptyPartner(): ProfilesPartner {
      return {
        id: '',
        locId: '',
        comId: '',
        oldId: 0,
        oldlocId: 0,
        oldcomId: 0,
        name: 'Loading',
        type: undefined,
        lat: 0,
        lng: 0,
        address1: '',
        address2: '',
        city: '',
        state: '',
        zipcode: '',
        district: '',
        phone1: '',
        phone2: '',
        fax: '',
        website: '',
        email: '',
        facebook: '',
        twitter: '',
        googleplus: '',
        youtube: '',
        othersocial: '',
        description: '',
        vision: '',
        mission: '',
        lodging: 0,
        capacity: 0,
        rooms: 0,
        showers: 0,
        toilets: 0,
        denomination: '',
        attendmax: 0,
        attendavg: 0,
        students: 0,
        classes: 0,
        rating: 0,
        value: 0,
        active: 0,
        resttype: '',
        rectype: '',
        residents: 0,
        units: 0,
        tuition: 0,
        cost: 0,
        hotelcap: 0,
        hotelrooms: 0,
        mercytype: '',
        parkLat: 0,
        parkLng: 0,
        parkNote: '',
        parkCost: 0,
        lodgingNotes: '',
        minChild: 0,
        minChildNotes: '',
        minServe: 0,
        minServeNotes: '',
        minChurch: 0,
        minChurchNotes: '',
        minSports: 0,
        minSportsNotes: '',
        minPrayer: 0,
        minPrayerNotes: '',
        minSchool: 0,
        minSchoolNotes: '',
        minMercy: 0,
        minMercyNotes: '',
        minEvang: 0,
        minEvangNotes: '',
        minYouth: 0,
        minYouthNotes: '',
        minAdult: 0,
        minAdultNotes: '',
        minMedical: 0,
        minMedicalNotes: '',
        minOther1: 0,
        minOther1Name: '',
        minOther1Notes: '',
        minOther2: 0,
        minOther2Name: '',
        minOther2Notes: '',
        descriptionPublic: '',
        visionPublic: '',
        missionPublic: '',
        modified: new Date(1000, 0, 1),
        photo: 0,
        wifi: 0,
        ssid: '',
        wifipass: '',
        inactive: 0,
        timezone: '',
        lodgingType: '',
        video: '',
        recDefault: 0,
        ppm365: 0,
        disaster: 0,
        funded: 0,
        numericId: 0,
        }
      }
  
    getAllProfilesPartner(): Observable<ProfilesPartner[]> {
      return this.http.get<ProfilesPartner[]>(`${this.config.dataApiUrl}/profiles-partners`);
    }

    getProfilesPartner(): Observable<ProfilesPartner[]> {
      return this.getAllProfilesPartner().pipe(
      tap(profilesPartner => this._profilesPartnerSubject.next(profilesPartner))
      );
    }

    getProfilesPartnerById(id: string): Observable<ProfilesPartner> {
      return this.http.get<ProfilesPartner>(`${this.config.dataApiUrl}/profiles-partners/${id}`);
    }
  
  
    updateProfilesPartner(profile: ProfilesPartner): Observable<ProfilesPartner> {
      return this.http.put<ProfilesPartner>(`${this.config.dataApiUrl}/profiles-partners/${profile.id}`, profile).pipe(
        tap(updated => this.partnerUpdated$.next(updated))
      );
    }

    updateProfilesPartnerPartial(
      partnerId: string,
      patch: Partial<ProfilesPartner>,
    ): Observable<ProfilesPartner> {
      return this.http
        .put<ProfilesPartner>(
          `${this.config.dataApiUrl}/profiles-partners/${encodeURIComponent(partnerId)}`,
          patch,
        )
        .pipe(tap((updated) => this.partnerUpdated$.next(updated)));
    }

    getPartnerTeams(partnerId: string): Observable<PartnerTeams[]> { 
      const teamDateFields: DateField<PartnerTeams>[] = [
        { fieldName: 'tripDate', dateType: 'local' }
      ];
      return this.http
        .get<PartnerTeams[]>(`${this.config.dataApiUrl}/profiles-partners/get-teams/${partnerId}`)
        .pipe(
          switchMap(teams => DataTransform.replaceDatesArray<PartnerTeams>(teams, teamDateFields))
        );
    }

    /** Trips linked to the partner (last 6 months) via Trips_partner. */
    getTripsByPartnerId(partnerId: string): Observable<TripDetail[]> {
      const tripDateFields: DateField<TripDetail>[] = [
        { fieldName: 'tripDate', dateType: 'local' },
        { fieldName: 'dateReserved', dateType: 'central' },
        { fieldName: 'tripEndDate', dateType: 'local' }
      ];
      return this.http.get<TripDetail[]>(`${this.config.dataApiUrl}/profiles-partners/${encodeURIComponent(partnerId)}/trips`).pipe(
        switchMap(trips => DataTransform.replaceDatesArray<TripDetail>(trips, tripDateFields))
      );
    }

    getPartnerContacts(partnerId: string): Observable<PartnerContact[]> {
      return this.http.get<PartnerContact[]>(`${this.config.dataApiUrl}/profiles-partners/contacts/${partnerId}`);
    }

    getPartnerContactById(contactId: string): Observable<FullPartnerContact> {
      return this.http.get<FullPartnerContact>(`${this.config.dataApiUrl}/profiles-partners/contacts/contact/${contactId}`);
    }

  setMainPpm365Contact(partnerId: string, contactId: string): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(
      `${this.config.dataApiUrl}/profiles-partners/${encodeURIComponent(partnerId)}/contacts/${encodeURIComponent(contactId)}/set-main-ppm365`,
      {}
    ).pipe(
      tap(() => this.partnerContactUpdated$.next({
        partnerId,
        contactId,
        operation: 'updated'
      }))
    );
  }

    createPartnerContact(contact: CreatePartnerContactParams): Observable<PartnerContact> {
      return this.http.post<PartnerContact>(`${this.config.dataApiUrl}/profiles-partners/contacts`, contact).pipe(
        tap(newContact => this.partnerContactUpdated$.next({ 
          partnerId: contact.partnerId, 
          contactId: newContact.id, 
          operation: "added" 
        }))
      );
    }

    updatePartnerContact(contactId: string, contact: UpdatePartnerContactParams): Observable<PartnerContact> {
      return this.http.put<PartnerContact>(`${this.config.dataApiUrl}/profiles-partners/contacts/${contactId}`, contact).pipe(
        tap(() => this.partnerContactUpdated$.next({ 
          partnerId: contact.partnerId, 
          contactId: contactId, 
          operation: "updated" 
        }))
      );
    }

    updatePartnerContactPartial(
      contactId: string,
      partnerId: string,
      patch: Partial<Omit<UpdatePartnerContactParams, 'id' | 'partnerId'>>,
    ): Observable<PartnerContact> {
      return this.http
        .put<PartnerContact>(
          `${this.config.dataApiUrl}/profiles-partners/contacts/${contactId}`,
          { id: contactId, partnerId, ...patch },
        )
        .pipe(
          tap(() =>
            this.partnerContactUpdated$.next({
              partnerId,
              contactId,
              operation: 'updated',
            }),
          ),
        );
    }

    createPartnerNeed(need: Partial<ProfilesPartnerNeed>): Observable<ProfilesPartnerNeed> {
      return this.http.post<ProfilesPartnerNeed>(`${this.config.dataApiUrl}/profiles-partners/needs`, need).pipe(
        tap(newNeed => this.partnerNeedUpdated$.next({
          partnerId: need.partnerId ?? newNeed.partnerId ?? '',
          needId: newNeed.id,
          operation: 'added'
        }))
      );
    }

    getPartnerNeeds(partnerId: string): Observable<ProfilesPartnerNeed[]> {
      const needDateFields: DateField<ProfilesPartnerNeed>[] = [
        { fieldName: 'date', dateType: 'local' },
        { fieldName: 'goaldate', dateType: 'local' },
        { fieldName: 'dateCompleted', dateType: 'local' },
        { fieldName: 'modified', dateType: 'local' }
      ];
      return this.http
        .get<ProfilesPartnerNeed[]>(`${this.config.dataApiUrl}/profiles-partners/needs/${partnerId}`)
        .pipe(
          switchMap(needs => DataTransform.replaceDatesArray<ProfilesPartnerNeed>(needs, needDateFields))
        );
    }

    deactivatePartnerNeed(partnerId: string, needId: string): Observable<{ success: boolean }> {
      return this.http.patch<{ success: boolean }>(`${this.config.dataApiUrl}/profiles-partners/needs/${needId}/deactivate`, {}).pipe(
        tap(() => this.partnerNeedUpdated$.next({ partnerId, needId, operation: 'deactivated' }))
      );
    }

    updatePartnerNeed(needId: string, need: Partial<ProfilesPartnerNeed>): Observable<ProfilesPartnerNeed> {
      return this.http.put<ProfilesPartnerNeed>(`${this.config.dataApiUrl}/profiles-partners/needs/${needId}`, need).pipe(
        tap(() => {
          this.partnerNeedUpdated$.next({
            partnerId: need.partnerId ?? '',
            needId,
            operation: 'updated'
          });
        })
      );
    }

    getPartnerEvents(partnerId: string): Observable<PartnerEvent[]> {
      const eventDateFields: DateField<PartnerEvent>[] = [
        { fieldName: 'start', dateType: 'local' },
        { fieldName: 'end', dateType: 'local' },
        { fieldName: 'modified', dateType: 'local' }
      ];
      return this.http
        .get<PartnerEvent[]>(`${this.config.dataApiUrl}/profiles-partners/events/${partnerId}`)
        .pipe(
          switchMap(events => DataTransform.replaceDatesArray<PartnerEvent>(events, eventDateFields))
        );
    }

    createPartnerEvent(event: Partial<PartnerEvent>): Observable<PartnerEvent> {
      return this.http.post<PartnerEvent>(`${this.config.dataApiUrl}/profiles-partners/events`, event).pipe(
        tap(created => this.partnerEventUpdated$.next({
          partnerId: event.partnerId ?? created.partnerId ?? '',
          eventId: created.id,
          operation: 'added'
        }))
      );
    }

    updatePartnerEvent(eventId: string, event: Partial<PartnerEvent>): Observable<PartnerEvent> {
      return this.http.put<PartnerEvent>(`${this.config.dataApiUrl}/profiles-partners/events/${eventId}`, event).pipe(
        tap(() => this.partnerEventUpdated$.next({
          partnerId: event.partnerId ?? '',
          eventId,
          operation: 'updated'
        }))
      );
    }

    deactivatePartnerEvent(partnerId: string, eventId: string): Observable<{ success: boolean }> {
      return this.http.patch<{ success: boolean }>(`${this.config.dataApiUrl}/profiles-partners/events/${eventId}/deactivate`, {}).pipe(
        tap(() => this.partnerEventUpdated$.next({ partnerId, eventId, operation: 'deactivated' }))
      );
    }

    getPartnerFiles(partnerId: string): Observable<{ files: PartnerFile[] }> {
      return this.http.get<{ files: PartnerFile[] }>(`${this.config.dataApiUrl}/profiles-partners/${partnerId}/files`);
    }

    uploadPartnerFile(partnerId: string, file: File): Observable<{ success: boolean; name: string }> {
      const formData = new FormData();
      formData.append('file', file, file.name);
      return this.http.post<{ success: boolean; name: string }>(
        `${this.config.dataApiUrl}/profiles-partners/${partnerId}/files`,
        formData
      );
    }

    deletePartnerFile(partnerId: string, filename: string): Observable<{ success: boolean }> {
      return this.http.delete<{ success: boolean }>(
        `${this.config.dataApiUrl}/profiles-partners/${partnerId}/files/${encodeURIComponent(filename)}`
      );
    }

    /** Status of the partner photos bucket (GCS). */
    getPartnerPhotosStatus(partnerId: string): Observable<PartnerPhotosStatus> {
      return this.http.get<PartnerPhotosStatus>(
        `${this.config.dataApiUrl}/profiles-partners/${encodeURIComponent(partnerId)}/photos/status`
      );
    }

    getPartnerPhotos(partnerId: string): Observable<{ photos: string[] }> {
      return this.http.get<{ photos: string[] }>(
        `${this.config.dataApiUrl}/profiles-partners/${encodeURIComponent(partnerId)}/photos`
      );
    }

    getPartnerPhotoSignedUrl(partnerId: string, filename: string): Observable<{ url: string }> {
      return this.http.get<{ url: string }>(
        `${this.config.dataApiUrl}/profiles-partners/${encodeURIComponent(partnerId)}/photos/${encodeURIComponent(filename)}`
      );
    }

    uploadPartnerPhoto(partnerId: string, file: File): Observable<{ success: boolean; name: string }> {
      const formData = new FormData();
      formData.append('file', file, file.name);
      return this.http.post<{ success: boolean; name: string }>(
        `${this.config.dataApiUrl}/profiles-partners/${encodeURIComponent(partnerId)}/photos`,
        formData
      );
    }

    deletePartnerPhoto(partnerId: string, filename: string): Observable<{ success: boolean }> {
      return this.http.delete<{ success: boolean }>(
        `${this.config.dataApiUrl}/profiles-partners/${encodeURIComponent(partnerId)}/photos/${encodeURIComponent(filename)}`
      );
    }

    getPartnerGalleryPhotos(partnerId: string): Observable<{ photos: string[] }> {
      return this.http.get<{ photos: string[] }>(
        `${this.config.dataApiUrl}/profiles-partners/${encodeURIComponent(partnerId)}/gallery`
      );
    }

    getPartnerGalleryPhotoSignedUrl(partnerId: string, filename: string): Observable<{ url: string }> {
      return this.http.get<{ url: string }>(
        `${this.config.dataApiUrl}/profiles-partners/${encodeURIComponent(partnerId)}/gallery/${encodeURIComponent(filename)}`
      );
    }

    uploadPartnerGalleryPhoto(partnerId: string, file: File, name?: string): Observable<{ success: boolean; name: string }> {
      const formData = new FormData();
      formData.append('file', file, file.name);
      if (name != null && String(name).trim() !== '') {
        formData.append('name', String(name).trim());
      }
      return this.http.post<{ success: boolean; name: string }>(
        `${this.config.dataApiUrl}/profiles-partners/${encodeURIComponent(partnerId)}/gallery`,
        formData
      );
    }

    deletePartnerGalleryPhoto(partnerId: string, filename: string): Observable<{ success: boolean }> {
      return this.http.delete<{ success: boolean }>(
        `${this.config.dataApiUrl}/profiles-partners/${encodeURIComponent(partnerId)}/gallery/${encodeURIComponent(filename)}`
      );
    }

    getContactPhotoSignedUrl(contactId: string): Observable<{ url: string }> {
      return this.http.get<{ url: string }>(
        `${this.config.dataApiUrl}/profiles-partners/contacts/contact/${encodeURIComponent(contactId)}/photo`
      );
    }

    uploadContactPhoto(contactId: string, file: File): Observable<{ success: boolean; name: string }> {
      const formData = new FormData();
      formData.append('file', file, file.name);
      return this.http.post<{ success: boolean; name: string }>(
        `${this.config.dataApiUrl}/profiles-partners/contacts/contact/${encodeURIComponent(contactId)}/photo`,
        formData
      );
    }

    deleteContactPhoto(contactId: string): Observable<{ success: boolean }> {
      return this.http.delete<{ success: boolean }>(
        `${this.config.dataApiUrl}/profiles-partners/contacts/contact/${encodeURIComponent(contactId)}/photo`
      );
    }

    getNeedPhotos(partnerId: string, needId: string): Observable<{ photos: string[] }> {
      return this.http.get<{ photos: string[] }>(
        `${this.config.dataApiUrl}/profiles-partners/needs/${encodeURIComponent(partnerId)}/${encodeURIComponent(needId)}/photos`
      );
    }

    /** Get signed URL for need photo. */
    getNeedPhotoSignedUrl(partnerId: string, needId: string): Observable<{ url: string }> {
      return this.http.get<{ url: string }>(
        `${this.config.dataApiUrl}/profiles-partners/needs/${encodeURIComponent(partnerId)}/${encodeURIComponent(needId)}/photo`
      );
    }

    uploadNeedPhoto(partnerId: string, needId: string, file: File): Observable<{ success: boolean; name: string }> {
      const formData = new FormData();
      formData.append('file', file, file.name);
      return this.http.post<{ success: boolean; name: string }>(
        `${this.config.dataApiUrl}/profiles-partners/needs/${encodeURIComponent(partnerId)}/${encodeURIComponent(needId)}/photo`,
        formData
      );
    }

    deleteNeedPhoto(partnerId: string, needId: string): Observable<{ success: boolean }> {
      return this.http.delete<{ success: boolean }>(
        `${this.config.dataApiUrl}/profiles-partners/needs/${encodeURIComponent(partnerId)}/${encodeURIComponent(needId)}/photo`
      );
    }

    getEventPhotos(partnerId: string, eventId: string): Observable<{ photos: string[] }> {
      return this.http.get<{ photos: string[] }>(
        `${this.config.dataApiUrl}/profiles-partners/events/${encodeURIComponent(partnerId)}/${encodeURIComponent(eventId)}/photos`
      );
    }

    /** Get signed URL for event photo. */
    getEventPhotoSignedUrl(partnerId: string, eventId: string): Observable<{ url: string }> {
      return this.http.get<{ url: string }>(
        `${this.config.dataApiUrl}/profiles-partners/events/${encodeURIComponent(partnerId)}/${encodeURIComponent(eventId)}/photo`
      );
    }

    uploadEventPhoto(partnerId: string, eventId: string, file: File): Observable<{ success: boolean; name: string }> {
      const formData = new FormData();
      formData.append('file', file, file.name);
      return this.http.post<{ success: boolean; name: string }>(
        `${this.config.dataApiUrl}/profiles-partners/events/${encodeURIComponent(partnerId)}/${encodeURIComponent(eventId)}/photo`,
        formData
      );
    }

    deleteEventPhoto(partnerId: string, eventId: string): Observable<{ success: boolean }> {
      return this.http.delete<{ success: boolean }>(
        `${this.config.dataApiUrl}/profiles-partners/events/${encodeURIComponent(partnerId)}/${encodeURIComponent(eventId)}/photo`
      );
    }

    getPartnerSupporters(partnerId: string): Observable<PartnerSupporter[]> {
      return this.http.get<PartnerSupporter[]>(`${this.config.dataApiUrl}/profiles-partners/${partnerId}/supporters`);
    }

    checkSupporterAccountExists(email: string): Observable<{ status: 'exists' | 'does-not-exist'; firstName?: string; lastName?: string }> {
      return this.http.get<{ status: 'exists' | 'does-not-exist'; firstName?: string; lastName?: string }>(
        `${this.config.dataApiUrl}/ppm365/supporters`,
        { params: { email } }
      );
    }

    addSupporter(body: { email: string; firstName: string; lastName: string; partnerId: string }): Observable<'success' | 'error' | 'account_exists'> {
      return this.http
        .post(`${this.config.dataApiUrl}/ppm365/supporters`, body, { responseType: 'text' })
        .pipe(map(result => (result as 'success' | 'error' | 'account_exists')));
    }

    removeSupporter(partnerId: string, newuserId: number): Observable<void> {
      return this.http.request<void>('DELETE', `${this.config.dataApiUrl}/ppm365/supporters/${partnerId}`, {
        body: { newuserId }
      });
    }
  }